import React, { useEffect, useState } from "react";
import { Link, ResponsiveHeroImage } from "@quintype/components";
import get from "lodash/get";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { FocusedImage } from "quintype-js";

import { clientWidth, isExternalStory, getStoryUrl, removeHtmlTags } from "../../../utils/utils";
import { FallbackImage } from "../FallbackImage";
import { HyperLink } from "../Hyperlink";
import { SvgIconHandler } from "../../../../components/atoms/SvgIconHandler";
import LiveButton from "../../../../components/atoms/live-button";
import { YTModal } from "../YTModal";
import { FALLBACK_IMAGE } from "../../../../constants";

import "./hero-image.m.css";

export const HeroImage = ({
  story,
  FullBleed,
  aspectRatio,
  defaultWidth,
  widths,
  isHorizontal,
  isHorizontalMobile,
  isHorizontalWithImageLast,
  defaultFallback,
  isStoryPageImage,
  showIcon,
  isRecommended = false,
  isYTPageImage = false,
  potraitStory = false,
  tagPage = false,
  firstTagPage = false,
  fetchPriorityHigh = false,
  isStoryHeader = false,
  isFeaturedImage = false,
  isPortraitImage = false,
  noHyperlink = false,
}) => {
  const fallbackImageUrl = FALLBACK_IMAGE;
  const [isDesktop, setIsDesktop] = useState(false);
  const [windowPresent, setWindowPresent] = useState(false);

  const cdnHost = useSelector((state) => get(state, ["qt", "config", "cdn-image"]));

  const showImagePlaceholder = useSelector((state) => get(state, ["qt", "config", "showPlaceholder"]));
  const getPlaceholderStyleName = showImagePlaceholder ? "placeholder" : "";

  const fullBleed = FullBleed ? "" : "with-padding";

  let heroImageClass = "hero-image";
  if (isHorizontal) heroImageClass = "right-padding";
  else if (isHorizontalWithImageLast) heroImageClass = "left-padding";

  const heroImageMobClasses = isHorizontalMobile ? "right-padding-mob" : "";

  const getPadding = (aspectRatio) => {
    const [height, width] = aspectRatio;
    if (height > 0 && width > 0) {
      const padding = (width / height) * 100;
      return padding;
    }
    return 0;
  };

  const [mobileAspectRatio, desktopAspectRatio = mobileAspectRatio] = aspectRatio;
  const isMobile = clientWidth("mobile");

  const imageAspectRatio = isMobile ? mobileAspectRatio : desktopAspectRatio;
  const imagePadding = isMobile ? getPadding(mobileAspectRatio) : getPadding(desktopAspectRatio);
  const slug =
    get(story, ["hero-image-s3-key"]) ||
    get(story, ["alternative", "home", "default", "hero-image", "hero-image-s3-key"]);
  const hyperLink = get(story, ["hero-image-hyperlink"], "");
  const imageCaption = get(story, ["hero-image-caption"], "");
  const altText = get(story, ["hero-image-alt-text"], "");
  const imageAltText = altText
    ? removeHtmlTags(altText)
    : imageCaption
    ? removeHtmlTags(imageCaption)
    : get(story, ["headline"], "");

  const getIcon = (story) => {
    const svgDimensions = isDesktop ? "24" : "20";
    switch (story?.["story-template"]) {
      case "video":
        return (
          <div styleName="video-icon">
            <i />
            {/* <SvgIconHandler
              type="story-play"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            /> */}
          </div>
        );
      case "book-review":
        return (
          <div styleName="book-review">
            <i />
            {/* <SvgIconHandler
              type="book-icon"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            /> */}
          </div>
        );
      case "review":
      case "movie-review":
        return (
          <div styleName="movie-review">
            <i />
            {/* <SvgIconHandler
              type="movie-icon"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            /> */}
          </div>
        );
      case "photo":
        return (
          <div styleName="photo-icon">
            <i />
            {/* <SvgIconHandler
              type="image"
              width={svgDimensions}
              height={svgDimensions}
              viewBox={`0 0 ${svgDimensions} ${svgDimensions}`}
            /> */}
          </div>
        );
      case "visual-story":
        return (
          <div styleName="story-type-tag">
            <i />
            {/* <SvgIconHandler type="visual-story" width={24} height={24} viewBox="0 0 24 24" /> */}
          </div>
        );
      case "live-blog":
        return <LiveButton story={story} isRecommended={isRecommended} />;
      default:
        break;
    }
  };

  useEffect(() => {
    if (window.innerWidth >= 1200) {
      setIsDesktop(true);
    }
    if (typeof window !== "undefined") {
      setWindowPresent(true);
    }
  }, []);

  let sizes;

  switch (true) {
    case tagPage && firstTagPage:
      sizes = "(max-width: 1160px) 100vw, 360px";
      break;
    case potraitStory:
      sizes = "(max-width: 1160px) 50vw, 267px";
      break;
    case tagPage:
      sizes = "(max-width: 1160px) 33vw, 360px";
      break;
  }

  const imageMeta = get(story, ["hero-image-metadata"], {});
  let rectParam = "";
  const HeroimageWidth = isPortraitImage ? 480 : isFeaturedImage || isStoryPageImage ? 640 : 360;
  const imageLoading = isStoryPageImage ? "eager" : "lazy";
  const fetchPriorityImg = isStoryPageImage || isFeaturedImage ? "high" : "auto";
  let metaRatio = 0;
  if (imageMeta) {
    const focusedImage = new FocusedImage(slug, imageMeta);
    const fullPath = focusedImage?.path([16, 9], {});
    const queryParams = fullPath?.split("?")[1];
    if (queryParams) {
      const params = new URLSearchParams(queryParams);
      const rect = params?.get("rect");
      if (rect) {
        rectParam = `rect=${rect}&`;
      }
    }
    if (imageMeta?.width > 1 && imageMeta?.height > 1) {
      metaRatio = imageMeta?.height / imageMeta?.width;
    }
  }

  const fallbackImage = () => {
    if (!slug) {
      if (fallbackImageUrl && !isStoryPageImage) {
        return (
          <figure
            styleName={`image ${getPlaceholderStyleName}`}
            className="arr--responsive-hero-image"
            style={{
              paddingTop: imagePadding + `%`,
            }}
          ></figure>
        );
      }

      return (
        <>
          {defaultFallback ? (
            <div
              styleName={`image ${getPlaceholderStyleName}`}
              className="arr--fallback-hero-image"
              data-test-id="arr--fallback-hero-image"
              style={{
                paddingTop: imagePadding + `%`,
              }}
            >
              {showIcon && getIcon(story)}
              <FallbackImage fetchPriorityHigh={fetchPriorityHigh || firstTagPage} />
            </div>
          ) : null}
        </>
      );
    }

    return (
      <>
        <figure
          styleName={`image ${getPlaceholderStyleName} ${isStoryHeader && metaRatio >= 1.3 ? "storyHeader" : ""}`}
          className="arr--responsive-hero-image"
          style={{ paddingTop: imagePadding + `%` }}
        >
          {showIcon && getIcon(story)}
          {!windowPresent && !sizes ? (
            <img
                src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=${HeroimageWidth}&auto=format,compress&q=80`}
                alt={story["hero-image-alt-text"] || `${story?.headline}`} title={story["hero-image-alt-text"] || story?.headline} loading={imageLoading}
                style={{ width: "100%" }} width="360" height="202" fetchpriority={fetchPriorityImg}
              />
          ) : !windowPresent && sizes ? (
            <picture>
              {potraitStory && (
                <>
                  <source
                    media="(max-width: 480px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=480&auto=format,compress&fit=max&q=80`}
                  />
                  <source
                    media="(min-width: 481px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                  />
                </>
              )}
              {tagPage && !firstTagPage && (
                <>
                  <source
                    media="(max-width: 379px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=250&auto=format,compress&fit=max&q=80`}
                  />
                  <source
                    media="(max-width: 726px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=480&auto=format,compress&fit=max&q=80`}
                  />
                  <source
                    media="(min-width: 727px)"
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                  />
                </>
              )}
              {tagPage && firstTagPage && (
                <>
                  <source
                    srcSet={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                  />
                </>
              )}
              <img
                src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=640&auto=format,compress&fit=max&q=80`}
                alt={story["hero-image-alt-text"] || story?.headline}
                style={{ width: "100%" }}
              />
            </picture>
          ) : (
             <img
                src={`https://${cdnHost}/${story?.["hero-image-s3-key"]}?${rectParam}w=${HeroimageWidth}&auto=format,compress&q=80`}
                alt={story["hero-image-alt-text"] || `${story?.headline}`} title={story["hero-image-alt-text"] || story?.headline} loading={imageLoading}
                style={{ width: "100%" }} width="360" height="202"  fetchpriority={fetchPriorityImg}
              />
          )}
        </figure>
        {isStoryPageImage && hyperLink && <HyperLink hyperLink={hyperLink} />}
      </>
    );
  };

  if (isYTPageImage) {
    return <YTModal story={story} aspectRatio={[[9, 16]]}></YTModal>;
  }

  return (
    story &&
    (isStoryPageImage || noHyperlink ? (
      <div
        className="arr--hero-image"
        data-test-id="arr--hero-image"
        styleName={`${heroImageClass} ${fullBleed} ${heroImageMobClasses}`}
        style={
          story?.["story-template"] === "review" || story?.["story-template"] === "movie-review"
            ? { marginTop: "0px" }
            : {}
        }
      >
        {fallbackImage()}
      </div>
    ) : (
      <Link
        className="arr--hero-image"
        data-test-id="arr--hero-image"
        href={getStoryUrl(story, `/${story.slug}`)}
        externalLink={isExternalStory(story)}
        styleName={`${heroImageClass} ${fullBleed} ${heroImageMobClasses}`}
        aria-label="fallback-hero-image"
        disableAjaxLinks={true}
      >
        {fallbackImage()}
      </Link>
    ))
  );
};

HeroImage.propTypes = {
  /** The Story Object from the API response */
  story: PropTypes.object.isRequired,
  /** Removes the Hero Image padding for the image to bleed over the margin */
  FullBleed: PropTypes.bool,
  /** The default width for the image, in case the browser does not support srcset */
  defaultWidth: PropTypes.number,
  /** The list of available widths */
  widths: PropTypes.array,
  // fatch the correct hero image
  aspectRatio: PropTypes.array,

  //  Hints for the browser to choose the best size
  /** This prop is responsible for adding padding-right of the image and remove padding bottom of the image  */
  isHorizontal: PropTypes.bool,
  /** This prop is responsible for adding padding-bottom in desktop and padding-right in mobile */
  isHorizontalMobile: PropTypes.bool,
  // this prop is responsive for rendering fallback image on story page
  defaultFallback: PropTypes.bool,
  isHorizontalWithImageLast: PropTypes.bool,
  isStoryPageImage: PropTypes.bool,
  showIcon: PropTypes.bool,
  tagPage: PropTypes.bool,
  fetchPriorityHigh: PropTypes.bool,
  firstTagPage: PropTypes.bool,
  potraitStory: PropTypes.bool,
  isStoryHeader: PropTypes.bool,
};

HeroImage.defaultProps = {
  FullBleed: true,
  aspectRatio: [
    [1, 1],
    [16, 9],
  ],
  defaultWidth: 480,
  widths: [250, 480, 640],
  isHorizontal: false,
  isHorizontalMobile: false,
  defaultFallback: true,
  isStoryPageImage: false,
  isHorizontalWithImageLast: false,
  showIcon: true,
};
